import CryptoJS from 'crypto-js';

// Get encryption key from environment variables.
// REMOVED FALLBACK: Ensure REACT_APP_ENCRYPTION_KEY is set in your environment.
const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

export const SENSITIVE_PROFILE_FIELDS = [
  'name', 'address1', 'address2', 'city', 'postcode',
  'latitude', 'longitude', 'bankName', 'accountNumber', 'sortCode',
  'lastInvoiceNumber', 'gdcNumber', 'insurancePolicyNumber', 'insuranceProvider'
];

// It's critical that SECRET_KEY is defined. Add a check.
if (!SECRET_KEY) {
  console.error("FATAL: REACT_APP_ENCRYPTION_KEY is not set. Encryption will not be secure.");
  // In a production build, you might want to throw an error here to halt the app
  // or use a clearly insecure, non-functional placeholder to make the issue obvious.
  // For now, this console error will highlight the problem during development.
} else {
  // Fail-fast self-test at module load: catches a broken/mismatched key once,
  // at startup, instead of re-testing on every encryptData() call.
  try {
    const selfTestPlaintext = '__encryption_self_test__';
    const selfTestCipher = CryptoJS.AES.encrypt(selfTestPlaintext, SECRET_KEY).toString();
    const selfTestResult = CryptoJS.AES.decrypt(selfTestCipher, SECRET_KEY).toString(CryptoJS.enc.Utf8);
    if (selfTestResult !== selfTestPlaintext) {
      console.error('FATAL: Encryption self-test failed at startup — REACT_APP_ENCRYPTION_KEY may be invalid.');
    }
  } catch (error) {
    console.error('FATAL: Encryption self-test threw an error at startup:', error);
  }
}

export const encryptData = (data) => {
  if (!data) return '';
  if (!SECRET_KEY) {
    throw new Error('Profile encryption is not configured');
  }
  // Ensure data is a string before encryption
  if (typeof data !== 'string') {
    data = String(data);
  }
  
  try {
    return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw error;
  }
};

export const encryptProfileFields = (profileData) => {
  const encrypted = { ...profileData };
  SENSITIVE_PROFILE_FIELDS.forEach((field) => {
    if (typeof encrypted[field] === 'string' && encrypted[field] && !isEncrypted(encrypted[field])) {
      encrypted[field] = encryptData(encrypted[field]);
    }
  });
  return encrypted;
};

export const decryptData = (data) => {
  if (!data) return '';
  // Ensure data is a string before attempting decryption
  if (typeof data !== 'string') {
    // If it's not a string, it's unlikely to be encrypted in the expected format.
    // console.warn('decryptData: Input is not a string, returning original data.', { type: typeof data, value: data });
    return data;
  }
  // A basic check to see if it might be an AES encrypted string (often starts with U2FsdGVkX1)
  if (!data.startsWith('U2FsdGVkX1')) {
    // console.warn('decryptData: Data does not appear to be encrypted (missing prefix), returning original data.');
    return data;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(data, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    // If decryption fails to produce valid Utf8 (wrong key, corrupted data),
    // `decrypted` will be empty. Returning the ciphertext in that case would
    // show garbled text to the user, so return an empty string instead.
    // NOTE: callers that persist this value back to Firestore (e.g. profile
    // save forms) MUST detect failure themselves (e.g. by checking whether the
    // original value looked encrypted but decrypted to '') and exclude that
    // field from the write payload — otherwise a failed decrypt silently wipes
    // the real encrypted value on next save. See ProfileSettings.jsx.
    if (!decrypted && data) {
      console.warn('Decryption produced empty result — returning empty string instead of ciphertext.', { dataPreview: data.substring(0, 10) + '...' });
    }
    return decrypted || '';
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Decryption error:', { errorMessage: error.message, dataPreview: data.substring(0, 20) + '...' });
    }
    return ''; // Fail closed — never expose raw ciphertext to the UI
  }
};

// Utility function to check if a value is likely encrypted by this utility
export const isEncrypted = (text) => {
  try {
    if (!text || typeof text !== 'string') return false;
    // Checks for the common prefix of CryptoJS AES encrypted strings
    // and that the rest of the string is valid Base64
    return /^U2FsdGVkX1[A-Za-z0-9+/=]+$/.test(text);
  } catch (error) {
    // console.error('Error in isEncrypted check:', error);
    return false;
  }
};